/**
 * SEMANTIC MOTION PRESETS — framer-motion variants and transitions named for
 * what they MEAN, not for what they do.
 *
 * A screen never says "fade 240ms". It says `page`, `modal`, `listItem`,
 * `inlineError`. The numbers live in ./tokens and are chosen once, here.
 *
 * Every variant set uses the same three labels so a component can be moved
 * from one preset to another without touching its markup:
 *   hidden  → the frame before it exists / after it has gone
 *   visible → at rest
 * Pages and wizard steps add direction and use enter / center / exit instead,
 * because their two edges differ.
 */
import type { Transition, Variants } from 'framer-motion';
import {
  DISTANCE,
  DURATION,
  EASE,
  OPACITY,
  SCALE,
  SPRING,
  STAGGER,
  tween,
} from './tokens';

/* ─────────────────────────── TRANSITIONS ──────────────────────────── */

export const T = {
  /** Something arriving: decelerates into place. */
  enter: tween(DURATION.emphasis, EASE.enter),
  /** Smaller things arriving. */
  enterFast: tween(DURATION.base, EASE.enter),
  /** Anything leaving. Always shorter than its entrance. */
  exit: tween(DURATION.exit, EASE.exit),
  /** A page's outgoing half. */
  pageExit: tween(DURATION.pageExit, EASE.exit),
  /** Hover / focus / colour: the micro tempo. */
  micro: tween(DURATION.fast, EASE.standard),
  /** Component state change at the standard tempo. */
  base: tween(DURATION.base, EASE.standard),
  /** The two or three hero moments. */
  cinematic: tween(DURATION.cinematic, EASE.enter),
  /** Physical containers. */
  spring: SPRING.standard as Transition,
  springGentle: SPRING.gentle as Transition,
  springSnappy: SPRING.snappy as Transition,
  indicator: SPRING.indicator as Transition,
} as const;

/* ────────────────────────────── PAGES ─────────────────────────────── */

/**
 * The spatial relationship between two screens. Chosen by the router layer
 * from where the user came FROM; a page never knows its own direction.
 *
 *  forward  a child screen reached from a menu — arrives from the right
 *  back     returning to the menu — arrives from the left
 *  down     the sibling below (visitor log → building activities)
 *  up       the sibling above
 *  deep     crossing an auth boundary (login → the app) — rises with presence
 *  none     unrelated screens — a soft crossfade with a hint of rise
 */
export type Direction =
  | 'forward'
  | 'back'
  | 'down'
  | 'up'
  | 'deep'
  | 'none';

const pageOffset = (dir: Direction): { x: number; y: number } => {
  switch (dir) {
    case 'forward':
      return { x: DISTANCE.medium, y: 0 };
    case 'back':
      return { x: -DISTANCE.medium, y: 0 };
    case 'down':
      return { x: 0, y: -DISTANCE.medium };
    case 'up':
      return { x: 0, y: DISTANCE.medium };
    case 'deep':
      return { x: 0, y: DISTANCE.large };
    default:
      return { x: 0, y: DISTANCE.small };
  }
};

export const page: Variants = {
  enter: (dir: Direction = 'none') => ({
    opacity: OPACITY.pageFrom,
    ...pageOffset(dir),
  }),
  center: (dir: Direction = 'none') => ({
    opacity: 1,
    x: 0,
    y: 0,
    transition: dir === 'deep' ? T.cinematic : dir === 'none' ? T.enterFast : T.enter,
  }),
  exit: (dir: Direction = 'none') => {
    const from = pageOffset(dir);

    // Leaving in the opposite direction to the arrival, a quarter as far.
    return {
      opacity: 0.4,
      x: -from.x / 4,
      y: -from.y / 4,
      transition: T.pageExit,
    };
  },
};

/* ─────────────────────────── WIZARD STEPS ─────────────────────────── */

export type StepDirection = 'forward' | 'back';

export const step: Variants = {
  enter: (dir: StepDirection = 'forward') => ({
    opacity: 0,
    x: dir === 'forward' ? DISTANCE.medium : -DISTANCE.medium,
  }),
  center: {
    opacity: 1,
    x: 0,
    transition: T.enterFast,
  },
  exit: (dir: StepDirection = 'forward') => ({
    opacity: 0,
    x: dir === 'forward' ? -DISTANCE.small : DISTANCE.small,
    transition: T.pageExit,
  }),
};

/* ───────────────────────────── OVERLAYS ───────────────────────────── */

export const scrim: Variants = {
  hidden: { opacity: 0, transition: T.exit },
  visible: { opacity: 1, transition: T.micro },
};

/** A centred dialog: focus, not zoom. */
export const modal: Variants = {
  hidden: {
    opacity: 0,
    scale: SCALE.modalFrom,
    y: DISTANCE.small,
    transition: T.exit,
  },
  visible: { opacity: 1, scale: 1, y: 0, transition: T.enterFast },
};

/** A sheet rising from the bottom edge (phones, the nav "More" sheet). */
export const sheet: Variants = {
  hidden: { y: '100%', transition: T.exit },
  visible: { y: 0, transition: tween(DURATION.emphasis, EASE.emphasized) },
};

/** A drawer sliding in from the right edge. */
export const drawerRight: Variants = {
  hidden: { x: '100%', transition: T.exit },
  visible: { x: 0, transition: tween(DURATION.emphasis, EASE.emphasized) },
};

export const drawerLeft: Variants = {
  hidden: { x: '-100%', transition: T.exit },
  visible: { x: 0, transition: tween(DURATION.emphasis, EASE.emphasized) },
};

/** A popover growing from its anchor. Set `transform-origin` at the site. */
export const popover: Variants = {
  hidden: {
    opacity: 0,
    scale: SCALE.popoverFrom,
    y: -DISTANCE.micro * 2,
    transition: tween(DURATION.instant, EASE.exit),
  },
  visible: { opacity: 1, scale: 1, y: 0, transition: T.micro },
};

/** A full-bleed takeover (the unprompted shift handover). */
export const takeover: Variants = {
  hidden: { opacity: 0, y: DISTANCE.medium, transition: T.exit },
  visible: { opacity: 1, y: 0, transition: T.enter },
};

/** A floating corner panel (Lobbero IQ). */
export const panel: Variants = {
  hidden: {
    opacity: 0,
    y: DISTANCE.medium,
    scale: 0.98,
    transition: T.exit,
  },
  visible: { opacity: 1, y: 0, scale: 1, transition: T.enterFast },
};

/* ───────────────────────── LOADING / STATES ───────────────────────── */

/** Skeleton ⇄ content ⇄ empty ⇄ error: the same box, a different fill. */
export const state: Variants = {
  hidden: { opacity: 0, transition: T.pageExit },
  visible: { opacity: 1, transition: T.enterFast },
};

/** The full-screen branded loader. (While it LEAVES it must not catch clicks —
    that is handled in branded-loader.tsx, not here, so the animation itself
    is unchanged.) */
export const loader: Variants = {
  hidden: { opacity: 0, transition: tween(DURATION.base, EASE.exit) },
  visible: { opacity: 1, transition: T.micro },
};

/* ─────────────────────────────── LISTS ────────────────────────────── */

/**
 * A row arriving. `custom` is the row index; the cascade stops at
 * STAGGER.maxItems so a 300-row table does not spend thirteen seconds
 * arriving.
 */
export const listItem: Variants = {
  hidden: { opacity: 0, y: DISTANCE.small },
  visible: (index = 0) => ({
    opacity: 1,
    y: 0,
    transition: {
      ...T.enterFast,
      delay: Math.min(index, STAGGER.maxItems) * STAGGER.list,
    },
  }),
  exit: {
    opacity: 0,
    x: -DISTANCE.small,
    transition: T.exit,
  },
};

/** A container whose children carry `listItem` variants. */
export const listContainer: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0 } },
};

/* ──────────────────────────── DISCLOSURE ──────────────────────────── */

export const collapse: Variants = {
  hidden: {
    height: 0,
    opacity: 0,
    transition: {
      height: tween(DURATION.base, EASE.standard),
      opacity: tween(DURATION.fast, EASE.exit),
    },
  },
  visible: {
    height: 'auto',
    opacity: 1,
    transition: {
      height: tween(DURATION.base, EASE.standard),
      opacity: tween(DURATION.base, EASE.enter, DURATION.instant),
    },
  },
};

/* ───────────────────────────── FEEDBACK ───────────────────────────── */

/** Invalid input. The field refuses; it does not explode. */
export const shake: Variants = {
  still: { x: 0 },
  shake: {
    x: [0, -6, 6, -4, 4, 0],
    transition: { duration: DURATION.emphasis / 1000, ease: 'easeInOut' },
  },
};

/** A tick or a confirmation mark landing. */
export const pop: Variants = {
  hidden: { scale: 0.6, opacity: 0 },
  visible: {
    scale: [0.6, SCALE.pop, 1],
    opacity: [0, 1, 1],
    transition: { duration: DURATION.base / 1000, ease: 'easeOut' },
  },
};

/** Inline validation text unfolding under its field. */
export const inlineMessage: Variants = collapse;
